// Formulário de criar/editar cliente.
// - RHF + zodResolver (schema do @control-center/shared — F1)
// - Máscaras inline em CPF/CNPJ/CEP/telefone (F5)
// - Validação onBlur (erros) + onChange (limpa erro quando válido) (F6)
// - Mensagens específicas (DESIGN_SYSTEM voz)
// - Autofocus no primeiro campo
// - Submit desabilitado enquanto pending

import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { HelpCircle, Loader2 } from 'lucide-react';
import type { z } from 'zod';

import type {
  preferredChannelSchema,
  clientStatusSchema,
  documentTypeSchema,
  clientTypeSchema,
} from '@control-center/shared';
import { BR_STATES, clientBaseSchema, createClientSchema } from '@control-center/shared';

import {
  maskCep,
  maskCpf,
  maskCnpj,
  maskPhone,
  unmaskDigits,
  unmaskDocument,
} from '../../lib/masks';

import { toast } from 'sonner';

import { firstErrorMessage } from '../../lib/form-errors';
import { Field, Input, Select, Textarea } from './form-primitives';
import { PillButton } from './PillButton';

// O schema do shared aceita number/string em version; pra create não usamos.
// Sobrescrevo só o `document_type` com z.input pra ter union estável no resolver.

const CLIENT_TYPE_OPTIONS: { value: z.infer<typeof clientTypeSchema>; label: string }[] = [
  { value: 'pessoa_fisica', label: 'Pessoa física' },
  { value: 'pessoa_juridica', label: 'Pessoa jurídica' },
];

const DOC_TYPE_OPTIONS: { value: z.infer<typeof documentTypeSchema>; label: string }[] = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'PASSPORT', label: 'Passaporte' },
  { value: 'OTHER', label: 'Outro' },
];

const STATUS_OPTIONS: { value: z.infer<typeof clientStatusSchema>; label: string }[] = [
  { value: 'lead', label: 'Lead' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'em_implantacao', label: 'Em implantação' },
  { value: 'em_teste', label: 'Em teste' },
  { value: 'inadimplente', label: 'Inadimplente' },
  { value: 'suspenso', label: 'Suspenso' },
  { value: 'cancelado', label: 'Cancelado' },
];

const CHANNEL_OPTIONS: {
  value: z.infer<typeof preferredChannelSchema>;
  label: string;
}[] = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'E-mail' },
  { value: 'ambos', label: 'Ambos' },
];

export type ClientFormValues = z.input<typeof createClientSchema>;
export type ClientFormSubmit = z.output<typeof createClientSchema>;

interface ClientFormProps {
  initialValues?: Partial<ClientFormValues>;
  isPending?: boolean;
  submitLabel?: string;
  /**
   * `true` = modo edição. Usa updateClientSchema (partial — não exige document quando
   * status muda pra ativo) e pula re-parse no submit.
   */
  editMode?: boolean;
  /**
   * Recebe o payload após o resolver — defaults aplicados, document normalizado.
   * Internamente re-parsamos com o schema pra garantir o shape do output.
   */
  onSubmit: (values: ClientFormSubmit) => void | Promise<void>;
  onCancel?: () => void;
}

const DEFAULT_VALUES: ClientFormValues = {
  type: 'pessoa_fisica',
  name: '',
  trade_name: null,
  document: null,
  document_type: 'CPF',
  inscricao_municipal: null,
  inscricao_estadual: null,
  email: null,
  fiscal_email: null,
  financial_email: null,
  phone: null,
  whatsapp: null,
  financial_whatsapp: null,
  preferred_channel: 'whatsapp',
  address_street: null,
  address_number: null,
  address_complement: null,
  address_neighborhood: null,
  address_city: null,
  address_state: null,
  address_zip_code: null,
  address_country: 'BR',
  responsavel: null,
  observations: null,
  status: 'ativo',
  is_iss_exempt: false,
  skip_dunning: false,
};

interface ViaCepResponse {
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean | string;
}

async function fetchViaCep(cep: string): Promise<ViaCepResponse | null> {
  const digits = cep.replace(/\D/g, '');
  if (digits.length !== 8) return null;
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!res.ok) return null;
    const data = (await res.json()) as ViaCepResponse;
    if (data.erro) return null;
    return data;
  } catch {
    return null;
  }
}

function SectionTitle({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h2 className="mb-3 text-[13px] font-semibold tracking-[-0.005em] text-ink">{children}</h2>
  );
}

function Card({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-[14px] border border-line bg-panel p-[20px]">{children}</section>
  );
}

/** Ícone "?" com tooltip nativo no hover. Pra hints curtos de campos do form. */
function HelpDot({ text }: { text: string }): JSX.Element {
  return (
    <span
      role="img"
      aria-label={text}
      title={text}
      tabIndex={0}
      className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full text-muted-3 transition-colors hover:text-muted focus:text-ink focus:outline-none"
    >
      <HelpCircle size={13} strokeWidth={1.8} />
    </span>
  );
}

export function ClientForm({
  initialValues,
  isPending,
  submitLabel = 'Salvar',
  editMode = false,
  onSubmit,
  onCancel,
}: ClientFormProps): JSX.Element {
  const merged: ClientFormValues = { ...DEFAULT_VALUES, ...initialValues };

  // No edit, schema partial — sem `version` obrigatório (esse campo não está
  // no form, é injetado pelo caller no submit) e sem refinement que exige
  // documento quando status != lead.
  const editFormSchema = clientBaseSchema.partial();
  const resolverSchema = editMode
    ? (editFormSchema as unknown as typeof createClientSchema)
    : createClientSchema;

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ClientFormValues>({
    resolver: zodResolver(resolverSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: merged,
  });

  // Autofocus no primeiro campo
  const firstFieldRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // Em edit, dispara validação inicial pra que `isValid` reflita os valores
  // pré-preenchidos sem precisar de blur. Sem isso, o botão Salvar nasce
  // desabilitado mesmo com tudo válido.
  useEffect(() => {
    if (editMode) {
      void trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  // CEP lookup via ViaCEP — preenche logradouro/bairro/cidade/UF quando atinge 8 dígitos.
  // Não sobrescreve campos já preenchidos (modo edição).
  const [isCepLoading, setIsCepLoading] = useState(false);
  const lastLookupRef = useRef<string>('');

  const handleCepLookup = async (rawCep: string): Promise<void> => {
    const digits = rawCep.replace(/\D/g, '');
    if (digits.length !== 8 || digits === lastLookupRef.current) return;
    lastLookupRef.current = digits;
    setIsCepLoading(true);
    try {
      const data = await fetchViaCep(digits);
      if (!data) return;
      const setIfEmpty = (
        field:
          | 'address_street'
          | 'address_neighborhood'
          | 'address_city'
          | 'address_state'
          | 'address_complement',
        value: string | undefined | null,
      ): void => {
        if (!value) return;
        const current = (watch(field) ?? '') as string;
        if (current && current.trim() !== '') return;
        setValue(field, value, { shouldValidate: true, shouldDirty: true });
      };
      setIfEmpty('address_street', data.logradouro);
      setIfEmpty('address_neighborhood', data.bairro);
      setIfEmpty('address_city', data.localidade);
      setIfEmpty('address_state', data.uf);
      setIfEmpty('address_complement', data.complemento);
    } finally {
      setIsCepLoading(false);
    }
  };

  const docType = watch('document_type') ?? 'CPF';
  const clientType = watch('type');

  // Sincroniza document_type com type quando muda PF → PJ e vice-versa,
  // desde que o usuário não tenha trocado pra Passaporte/Outro manualmente.
  useEffect(() => {
    if (clientType === 'pessoa_fisica' && (docType === 'CNPJ' || docType === 'OTHER')) {
      setValue('document_type', 'CPF', { shouldValidate: false });
    } else if (clientType === 'pessoa_juridica' && docType === 'CPF') {
      setValue('document_type', 'CNPJ', { shouldValidate: false });
    }
    // intencional: depende só de clientType
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientType]);

  const docMaskedPlaceholder =
    docType === 'CPF' ? '000.000.000-00' : docType === 'CNPJ' ? '00.000.000/0000-00' : 'Documento';

  const { ref: nameRefRHF, ...nameRest } = register('name');

  const handleValid = (values: ClientFormValues): void | Promise<void> => {
    if (editMode) {
      // Em edit, o caller monta o payload final (incluindo version) e o backend
      // valida com updateClientSchema. Não re-parsamos aqui pra não disparar o
      // refinement de documento do createClientSchema.
      return onSubmit(values as ClientFormSubmit);
    }
    // Re-parse via Zod aplica defaults e normaliza document (transform `normalizeDocument`).
    const parsed = createClientSchema.parse(values);
    return onSubmit(parsed);
  };

  return (
    <form
      onSubmit={handleSubmit(handleValid, (errs) =>
        toast.error(firstErrorMessage(errs) ?? 'Formulário tem campos inválidos.'),
      )}
      className="flex flex-col gap-4"
      noValidate
    >
      {/* Identificação */}
      <Card>
        <SectionTitle>Identificação</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Nome" required error={errors.name?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Nome completo ou razão social"
                hasError={!!errors.name}
                ref={(el) => {
                  nameRefRHF(el);
                  firstFieldRef.current = el;
                }}
                {...nameRest}
              />
            )}
          </Field>

          <Field label="Tipo" required error={errors.type?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.type}
                {...register('type')}
              >
                {CLIENT_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Nome fantasia" error={errors.trade_name?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Opcional"
                hasError={!!errors.trade_name}
                {...register('trade_name', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Tipo de documento" error={errors.document_type?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.document_type}
                {...register('document_type')}
              >
                {DOC_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Documento" error={errors.document?.message}>
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="document"
                render={({ field }) => {
                  const raw = field.value ?? '';
                  const display =
                    docType === 'CPF' ? maskCpf(raw) : docType === 'CNPJ' ? maskCnpj(raw) : raw;
                  return (
                    <Input
                      id={inputId}
                      aria-describedby={describedBy}
                      tabular
                      placeholder={docMaskedPlaceholder}
                      hasError={!!errors.document}
                      inputMode={docType === 'CPF' ? 'numeric' : 'text'}
                      value={display}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        const next =
                          docType === 'CPF' || docType === 'CNPJ'
                            ? unmaskDocument(e.target.value)
                            : e.target.value;
                        field.onChange(next || null);
                      }}
                    />
                  );
                }}
              />
            )}
          </Field>

          <Field label="Inscrição Estadual" error={errors.inscricao_estadual?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Opcional"
                tabular
                hasError={!!errors.inscricao_estadual}
                {...register('inscricao_estadual', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Inscrição Municipal" error={errors.inscricao_municipal?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Opcional"
                tabular
                hasError={!!errors.inscricao_municipal}
                {...register('inscricao_municipal', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Status" required error={errors.status?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.status}
                {...register('status')}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {/* Contato */}
      <Card>
        <SectionTitle>Contato</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="E-mail principal" error={errors.email?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                placeholder="contato@empresa.com"
                hasError={!!errors.email}
                {...register('email', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="E-mail fiscal" error={errors.fiscal_email?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                placeholder="fiscal@empresa.com"
                hasError={!!errors.fiscal_email}
                {...register('fiscal_email', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="E-mail financeiro" error={errors.financial_email?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                type="email"
                inputMode="email"
                placeholder="financeiro@empresa.com"
                hasError={!!errors.financial_email}
                {...register('financial_email', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Canal preferido" error={errors.preferred_channel?.message}>
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.preferred_channel}
                {...register('preferred_channel')}
              >
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Telefone" error={errors.phone?.message}>
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    tabular
                    inputMode="tel"
                    placeholder="(00) 0000-0000"
                    hasError={!!errors.phone}
                    value={maskPhone(field.value ?? '')}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(unmaskDigits(e.target.value) || null)}
                  />
                )}
              />
            )}
          </Field>

          <Field label="WhatsApp" error={errors.whatsapp?.message}>
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="whatsapp"
                render={({ field }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    tabular
                    inputMode="tel"
                    placeholder="(00) 00000-0000"
                    hasError={!!errors.whatsapp}
                    value={maskPhone(field.value ?? '')}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(unmaskDigits(e.target.value) || null)}
                  />
                )}
              />
            )}
          </Field>

          <Field label="WhatsApp financeiro" error={errors.financial_whatsapp?.message}>
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="financial_whatsapp"
                render={({ field }) => (
                  <Input
                    id={inputId}
                    aria-describedby={describedBy}
                    tabular
                    inputMode="tel"
                    placeholder="(00) 00000-0000"
                    hasError={!!errors.financial_whatsapp}
                    value={maskPhone(field.value ?? '')}
                    onBlur={field.onBlur}
                    onChange={(e) => field.onChange(unmaskDigits(e.target.value) || null)}
                  />
                )}
              />
            )}
          </Field>
        </div>
      </Card>

      {/* Endereço */}
      <Card>
        <SectionTitle>Endereço</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
          <Field
            label="CEP"
            error={errors.address_zip_code?.message}
            hint={isCepLoading ? 'Buscando endereço…' : 'Preenche o endereço automaticamente'}
            className="md:col-span-2"
          >
            {({ inputId, describedBy }) => (
              <Controller
                control={control}
                name="address_zip_code"
                render={({ field }) => (
                  <div className="relative">
                    <Input
                      id={inputId}
                      aria-describedby={describedBy}
                      tabular
                      inputMode="numeric"
                      placeholder="00000-000"
                      hasError={!!errors.address_zip_code}
                      value={maskCep(field.value ?? '')}
                      onBlur={field.onBlur}
                      onChange={(e) => {
                        const digits = unmaskDigits(e.target.value);
                        field.onChange(digits || null);
                        if (digits.length === 8) {
                          void handleCepLookup(digits);
                        }
                      }}
                    />
                    {isCepLoading && (
                      <span
                        aria-hidden
                        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted"
                      >
                        <Loader2 size={14} className="animate-spin" strokeWidth={2} />
                      </span>
                    )}
                  </div>
                )}
              />
            )}
          </Field>

          <Field
            label="Logradouro"
            error={errors.address_street?.message}
            className="md:col-span-3"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Rua, Av., etc."
                hasError={!!errors.address_street}
                {...register('address_street', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Número" error={errors.address_number?.message} className="md:col-span-1">
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="—"
                hasError={!!errors.address_number}
                {...register('address_number', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field
            label="Complemento"
            error={errors.address_complement?.message}
            className="md:col-span-3"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Sala, andar, etc."
                hasError={!!errors.address_complement}
                {...register('address_complement', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field
            label="Bairro"
            error={errors.address_neighborhood?.message}
            className="md:col-span-3"
          >
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.address_neighborhood}
                {...register('address_neighborhood', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Cidade" error={errors.address_city?.message} className="md:col-span-4">
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.address_city}
                {...register('address_city', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="UF" error={errors.address_state?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Select
                id={inputId}
                aria-describedby={describedBy}
                hasError={!!errors.address_state}
                defaultValue=""
                {...register('address_state', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              >
                <option value="">—</option>
                {BR_STATES.map((uf) => (
                  <option key={uf} value={uf}>
                    {uf}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Card>

      {/* Operacional */}
      <Card>
        <SectionTitle>Operacional</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Responsável" error={errors.responsavel?.message}>
            {({ inputId, describedBy }) => (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                placeholder="Nome do contato principal"
                hasError={!!errors.responsavel}
                {...register('responsavel', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <Field label="Observações" error={errors.observations?.message} className="md:col-span-2">
            {({ inputId, describedBy }) => (
              <Textarea
                id={inputId}
                aria-describedby={describedBy}
                rows={3}
                placeholder="Notas internas (não aparecem pro cliente)"
                hasError={!!errors.observations}
                {...register('observations', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            )}
          </Field>

          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line bg-panel accent-ink"
              {...register('is_iss_exempt')}
            />
            Isento de ISS
            <HelpDot
              text={
                'Marque quando o cliente é desobrigado de pagar ISS (Imposto Sobre Serviços) — ' +
                'ex: MEI prestador, empresa enquadrada no Simples com substituição tributária, ou ' +
                'serviço com imunidade. Quando marcado, a NFS-e é emitida sem retenção/dedução de ISS.'
              }
            />
          </label>
          <label className="flex items-center gap-2 text-[13px] text-ink-2">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line bg-panel accent-ink"
              {...register('skip_dunning')}
            />
            Não disparar régua de cobrança
            <HelpDot
              text={
                'A "régua de cobrança" é a sequência automática de lembretes ' +
                '(e-mail + WhatsApp) que o sistema envia quando uma cobrança vence ' +
                'sem pagamento — D+1, D+3, D+7 e suspensão D+15. ' +
                'Marque pra clientes especiais (parceiros, cortesia, casos negociados manualmente) ' +
                'que você prefere acompanhar pessoalmente.'
              }
            />
          </label>
        </div>
      </Card>

      <div className="sticky bottom-0 -mx-[22px] flex items-center justify-end gap-2 border-t border-line bg-page/90 px-[22px] py-3 backdrop-blur">
        {onCancel && (
          <PillButton onClick={onCancel} disabled={isPending || isSubmitting}>
            Cancelar
          </PillButton>
        )}
        <PillButton
          solid
          type="submit"
          disabled={isPending || isSubmitting || (!editMode && !isValid)}
        >
          {isPending || isSubmitting ? 'Salvando…' : submitLabel}
        </PillButton>
      </div>
    </form>
  );
}
